import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  IndicataResponse,
  Link,
  Relation,
} from '@/global/tradein/indicata.interface';
import { RootState } from '@/store/redux';
import {
  createSkippedStepObj,
  FormState,
  getLowerHierarchyRelations,
  getMockedColors,
  getMockYears,
  mockMonths,
  Option,
  RelName,
  translateValues,
} from '@/store/redux/slices/pages/tradein/sequence';

export interface VinState {
  steps: FormState;
  vin: string;
  vinRequiredError?: boolean;
}

// The relation order matters (the hierarchy is deduced by it)
export const initialState: VinState = {
  vin: '',
  vinRequiredError: false,
  steps: {
    make: {
      selectedOption: null,
      disabled: true,
      options: [],
      hidden: true,
    },
    model: {
      selectedOption: null,
      disabled: true,
      options: [],
      hidden: true,
    },
    colors: {
      selectedOption: null,
      disabled: true,
      options: [],
      hidden: false,
      required: true,
    },
    regmonth: {
      selectedOption: null,
      disabled: true,
      options: [],
      hidden: true,
    },
    regdate: {
      selectedOption: null,
      disabled: true,
      options: [],
      hidden: true,
    },
    body: {
      selectedOption: null,
      disabled: true,
      options: [],
      hidden: true,
    },
    facelift: {
      selectedOption: null,
      disabled: true,
      options: [],
      hidden: true,
    },
    seats: {
      selectedOption: null,
      disabled: true,
      options: [],
      hidden: true,
    },
    bodyHeight: {
      selectedOption: null,
      disabled: true,
      options: [],
      hidden: true,
    },
    bodyLength: {
      selectedOption: null,
      disabled: true,
      options: [],
      hidden: true,
    },
    weight: {
      selectedOption: null,
      disabled: true,
      options: [],
      hidden: true,
    },
    engine: {
      selectedOption: null,
      disabled: true,
      options: [],
      hidden: true,
    },
    wheeldrive: {
      selectedOption: null,
      disabled: true,
      options: [],
      hidden: true,
    },
    transmission: {
      selectedOption: null,
      disabled: true,
      options: [],
      hidden: true,
    },
    trim: {
      selectedOption: null,
      disabled: true,
      options: [],
      hidden: true,
    },
  },
};

const buildSimplePrevStep = (key: number, link: Link) => {
  return {
    key: [key],
    name: key.toString(),
    summary: (key < 10 ? '0' : '') + key,
    assumed: false,
    link,
  };
};

const normalizeApiResponse = (response: IndicataResponse) => {
  const data = { ...response };
  if (
    data.regDate &&
    data.regDate.key &&
    typeof data.regDate.key === 'string'
  ) {
    const [realYear, realMonth] = data.regDate.key.split('-');

    const realYearNumber = parseInt(realYear, 10);
    data.regdate = buildSimplePrevStep(realYearNumber, data.regDate.link);

    if (realMonth) {
      const realMonthNumber = parseInt(realMonth, 10);
      data.regmonth = buildSimplePrevStep(realMonthNumber, data.regDate.link);
    }
    delete data.regDate;
  }
  return data;
};

const getMockedOptions = (relationName: string, receivedValue: string) => {
  if (relationName === Relation.RegDate) {
    return getMockYears(parseInt(receivedValue));
  }

  if (relationName === Relation.RegMonth) {
    return mockMonths;
  }

  if (receivedValue === Relation.Colors) {
    return getMockedColors();
  }

  return [];
};

export function isColorStep(length: number, compareValue: string): boolean {
  if (length > 0) {
    const colors: string[] = [];
    colors.push(...colors, ...getMockedColors().map((col) => col.name));
    return colors.includes(translateValues(compareValue));
  }
  return false;
}

/**
 * Reflects Indicata VIN API data and current form state
 */
const slice = createSlice({
  name: 'tradein/vin',
  initialState,
  reducers: {
    // Update Indicata API sequence data and form state by next step
    populateVinForm(state, action: PayloadAction<IndicataResponse>) {
      const response = action.payload;
      const isRequired = !response.valuation;
      const relation: Relation = response?.nextStep[0]?.rel;
      const partialStepsState: Partial<FormState> = {};
      const data = normalizeApiResponse(response);

      // Iterate all relations in order to find skipped steps
      // Merge skipped steps into partialState as disabled form fields
      for (const relationName in initialState.steps) {
        const rel = relationName as RelName;

        if (data[rel] && !state.steps[rel].selectedOption) {
          const skippedStep = data[rel]!;
          partialStepsState[rel] = createSkippedStepObj(
            skippedStep,
            rel,
            state.steps[rel].selectedOption,
            getMockedOptions(relationName, skippedStep.name),
            true
          );
        }
      }

      const nextRelationObj = relation
        ? {
            [relation]: {
              disabled: false,
              options: action.payload?.nextStep,
              selectedOption: null,
              hidden: false,
              required: isRequired,
            },
          }
        : null;

      return {
        ...state,
        vinRequiredError: false,
        steps: {
          ...state.steps,
          ...partialStepsState,
          ...nextRelationObj,
        },
      };
    },
    // Reset sequence form to initial
    resetVinForm() {
      return initialState;
    },
    resetVinFormSteps(state, action: PayloadAction<boolean>) {
      const fullReset = action.payload;
      return {
        ...state,
        steps: {
          ...initialState.steps,
          regdate: fullReset ? initialState.steps.regdate : state.steps.regdate,
          regmonth: fullReset
            ? initialState.steps.regmonth
            : state.steps.regmonth,
        },
      };
    },
    // Select an item for the given relation
    selectVinFormOption(
      state,
      action: PayloadAction<{
        relationName: RelName;
        selectedOption: Option;
      }>
    ) {
      const relation: RelName = action.payload?.relationName;
      const option: Option = action.payload?.selectedOption;

      return {
        ...state,
        steps: {
          ...state.steps,
          [relation]: {
            disabled: state.steps[relation].disabled,
            options: state.steps[relation].options,
            selectedOption: option,
            hidden: state.steps[relation].hidden,
            required: state.steps[relation].required,
          },
        },
      };
    },
    // Deselect (reset to null) the item for the given relation
    deselectVinFormOption(
      state,
      action: PayloadAction<{ relationName: RelName }>
    ) {
      const relation: RelName = action.payload?.relationName;
      const partialInitialStepState = getLowerHierarchyRelations(relation);

      return {
        ...state,
        steps: {
          ...state.steps,
          ...partialInitialStepState,
          [relation]: {
            disabled: state.steps[relation].disabled,
            options: state.steps[relation].options,
            selectedOption: null,
            hidden: state.steps[relation].hidden,
          },
        },
      };
    },
    setRequiredVinFormFields(state) {
      const partialState: Partial<FormState> = {};

      // Iterate all relations in order to find missing user inputs
      for (const relationName in initialState.steps) {
        const relation = relationName as RelName;

        if (
          !state.steps[relation].hidden &&
          state.steps[relation].required &&
          !state.steps[relation].selectedOption
        ) {
          partialState[relation] = {
            disabled: state.steps[relation].disabled,
            options: state.steps[relation].options,
            selectedOption: state.steps[relation].selectedOption,
            hidden: state.steps[relation].hidden,
            required: state.steps[relation].required,
            error: true,
          };
        }
      }

      return {
        ...state,
        vinRequiredError: !state.vin.length,
        steps: {
          ...state.steps,
          ...partialState,
        },
      };
    },
    setVin(state, action: PayloadAction<string>) {
      const vin: string = action.payload;

      return {
        ...state,
        vinRequiredError: false,
        vin,
      };
    },
  },
});

export const {
  populateVinForm,
  resetVinForm,
  resetVinFormSteps,
  selectVinFormOption,
  deselectVinFormOption,
  setRequiredVinFormFields,
  setVin,
} = slice.actions;

export const getTradeInVinState = (state: RootState): VinState =>
  state.pages.tradeIn.vin;

export default slice.reducer;
